// ─────────────────────────────────────────────────────────────────────────────
// INSTRUMENT: Emotional Intelligence Assessment
// Framework:  Goleman-inspired (5 domains, 19 subscales)
// Items:      96 statements, 5-point Likert
// Output:     5 EQ domain scores + 19 subscale scores + overall composite
// ─────────────────────────────────────────────────────────────────────────────
import { QUESTIONS, QUESTIONS_PER_PAGE, TOTAL_PAGES } from "./questions.js";
import { DOMAINS, DOMAIN_MAP, NORMS } from "./factors.js";
import { scoreEQ } from "./scoring.js";
import { NINEBOX_CELLS } from "../../constants/competencies.js";

function buildAIPrompt(assessment, reportType) {
  const scales    = assessment.scores || {};
  const subscales = assessment.facets || {};
  const cell      = NINEBOX_CELLS.find(c => c.x===assessment.perfBox && c.y===assessment.potBox);

  const domainLines = DOMAINS.map(d => {
    const s = scales[d.id] || "—";
    const label = !s ? "—" : s <= 3 ? "LOW" : s >= 8 ? "HIGH" : "MODERATE";
    return `  • ${d.name} (${d.id}): ${s}/10 [${label}] — ${s<=5?d.lowLabel:d.highLabel}`;
  }).join("\n");

  const subLines = Object.entries(subscales)
    .map(([sub, score]) => `  • ${sub}: ${score}/10`)
    .join("\n");

  return `You are an expert occupational psychologist interpreting an Emotional Intelligence (EQ) Assessment based on the Goleman framework.

${reportType === "hiring"
  ? "HIRING REPORT — assess emotional intelligence fit for role, provide recommendation and interview probes targeting EQ competencies."
  : "DEVELOPMENT REPORT — focus on EQ growth areas, leveraging emotional strengths and a personalised development plan."}

INSTRUMENT: Emotional Intelligence Assessment (96 items, Goleman-inspired framework)
CANDIDATE: ${assessment.name} | ${assessment.role} | ${assessment.demo?.country || ""} | ${assessment.demo?.industry || ""}
9-BOX: ${cell?.label || "—"} | Self Axis: ${assessment.perfAvg} | Social Axis: ${assessment.potAvg}

FIVE EQ DOMAIN SCORES (1-10):
${domainLines}

SUBSCALE SCORES (1-10):
${subLines}

NOTE: SA=Self-Awareness, SR=Self-Regulation, MO=Motivation, EM=Empathy, SS=Social Skills.
High scores across all 5 domains = high overall EQ. Self-axis (SA+SR) drives individual effectiveness; Social axis (EM+SS) drives relational effectiveness.`;
}

const EQ = {
  id:          "eq",
  name:        "Emotional Intelligence Assessment",
  shortName:   "EQ",
  version:     "1.0",
  publisher:   "AXIS360 — Goleman Framework",
  description: "96-item self-report EQ assessment measuring five fundamental domains of emotional intelligence: Self-Awareness, Self-Regulation, Motivation, Empathy and Social Skills — the cornerstone of effective leadership and high-performance relationships.",
  icon:        "💡",
  color:       "#dc2626",
  bg:          "#fef2f2",

  totalQuestions: 96,
  timeEstimate:   "15–20 min",
  scaleType:      "likert-5",
  pagesOf:        QUESTIONS_PER_PAGE,
  totalPages:     TOTAL_PAGES,

  questions:  QUESTIONS,
  scales:     DOMAINS,
  scaleMap:   DOMAIN_MAP,
  domains:    DOMAINS,
  norms:      NORMS,

  nineboxAxes: {
    x: { label:"Self Effectiveness",   description:"Self-Awareness + Self-Regulation", scaleIds:["SA","SR"] },
    y: { label:"Social Effectiveness", description:"Empathy + Social Skills",          scaleIds:["EM","SS"] },
  },
  nineboxCells: NINEBOX_CELLS,
  competencies: null,

  score:         scoreEQ,
  buildAIPrompt,

  answerFormat: {
    type: "likert-5",
    options: [
      { value:1, label:"Strongly Disagree",   short:"1" },
      { value:2, label:"Disagree",             short:"2" },
      { value:3, label:"Neutral",              short:"3" },
      { value:4, label:"Agree",                short:"4" },
      { value:5, label:"Strongly Agree",       short:"5" },
    ],
  },
};

export default EQ;
